import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { ResizeSensor } from "@blueprintjs/core";
import { Suggest as BlueprintSuggest, } from "@blueprintjs/select";
import { defaultCreateItem, defaultItemPredicate, defaultItemRender, defaultListRenderer, } from "../utils";
import { getTextContentFromHtml } from "utils/string";
import { fireOnEnter } from "utils/keyboard";
import { closeSelectPopover, getHideListenerModifier } from "utils/popper";
import * as styles from "../styles.module.scss";
const Suggest = ({ viewMode = false, items, selectedItem, closeOnEnter, onItemSelect, itemRenderer, renderAdditionalListItem, itemPredicate, itemListPredicate, useExternalQueryForPredicate, createNewItemFromQuery, createNewItemRenderer, initialContent, popoverProps, query, itemEqual = "value", resetOnSelect, resetOnClose, resetOnQuery, onQueryChange, defaultItemRenderedProps, inputProps, classes, maxQueryLength, showRemainingSymbols, rightElement, disableCreateNew, disabled, noResultsRenderer, listProps, }) => {
    const rootRef = useRef(null);
    const suggestRef = useRef(null);
    const [wrapperWidth, setWrapperWidth] = useState();
    const [popoverRef, setPopoverRef] = useState(null);
    const [remainingSymbols, setRemainingSymbols] = useState(maxQueryLength ?? 0);
    useLayoutEffect(() => {
        if (popoverRef && wrapperWidth) {
            popoverRef.style.minWidth = `${wrapperWidth}px`;
        }
    }, [popoverRef, wrapperWidth]);
    useLayoutEffect(() => {
        const { current } = rootRef;
        if (current !== null) {
            const input = current.querySelector("input");
            if (maxQueryLength) {
                input?.setAttribute("maxlength", `${maxQueryLength}`);
            }
        }
    }, [maxQueryLength]);
    const handleResize = useCallback((entries) => {
        const { current } = rootRef;
        if (current) {
            const entry = entries.find(({ target }) => target === current);
            entry && setWrapperWidth(entry.contentRect.width);
        }
    }, []);
    const handleCloseOnEnter = useCallback((e) => fireOnEnter(e, () => closeSelectPopover(suggestRef)), []);
    const hideListener = useMemo(() => getHideListenerModifier(closeSelectPopover(suggestRef)), []);
    const handleQueryChange = useCallback((query) => {
        onQueryChange && onQueryChange(query);
        if (maxQueryLength) {
            const remainingSymbols = maxQueryLength - (query?.length || 0);
            setRemainingSymbols(remainingSymbols);
            const { current } = rootRef;
            if (showRemainingSymbols && current) {
                if (remainingSymbols < 0) {
                    current.classList.add(styles.error);
                }
                else {
                    current.classList.remove(styles.error);
                }
            }
        }
    }, [maxQueryLength, onQueryChange, showRemainingSymbols]);
    const inputValueRenderer = useCallback((item) => getTextContentFromHtml(typeof item === "string" || typeof item === "number"
        ? (items.find(({ value }) => item === value?.value)?.label ?? "")
        : item.label) ?? "", [items]);
    return (React.createElement(ResizeSensor, { onResize: handleResize },
        React.createElement("div", { ref: rootRef, className: classNames(styles.selectWrapper, styles.suggest) },
            React.createElement(BlueprintSuggest, { ref: suggestRef, disabled: viewMode || disabled, className: classNames(styles.root, classes?.className, {
                    [styles.viewMode]: viewMode,
                }), createNewItemFromQuery: disableCreateNew ? undefined : createNewItemFromQuery || defaultCreateItem, createNewItemRenderer: disableCreateNew ? undefined : createNewItemRenderer, onQueryChange: handleQueryChange, initialContent: initialContent, itemRenderer: (item, itemProps) => (itemRenderer && itemRenderer({ ...item, ...defaultItemRenderedProps }, itemProps)) ||
                    defaultItemRender(item, itemProps, true), itemPredicate: (predicateQuery, ...restArgs) => {
                    const preparedQuery = useExternalQueryForPredicate ? (query ?? predicateQuery) : predicateQuery;
                    return itemPredicate
                        ? itemPredicate(preparedQuery, ...restArgs)
                        : defaultItemPredicate(preparedQuery, ...restArgs);
                }, itemListRenderer: (props) => viewMode
                    ? null
                    : defaultListRenderer(props, popoverRef, disableCreateNew, renderAdditionalListItem, noResultsRenderer, itemListPredicate, listProps), itemListPredicate: itemListPredicate, inputValueRenderer: inputValueRenderer, itemsEqual: itemEqual, items: items, onItemSelect: onItemSelect, inputProps: {
                    rightElement: !viewMode ? (React.createElement(React.Fragment, null,
                        rightElement,
                        showRemainingSymbols && React.createElement("p", { className: styles.remaining },
                            "(",
                            remainingSymbols,
                            ")"))) : undefined,
                    onKeyDown: (e) => {
                        closeOnEnter && handleCloseOnEnter(e);
                        inputProps?.onKeyDown && inputProps.onKeyDown(e);
                    },
                    tabIndex: 0,
                    ...inputProps,
                }, popoverProps: {
                    popoverRef: setPopoverRef,
                    portalClassName: classNames(styles.portal, classes?.portalClassName),
                    popoverClassName: classNames(styles.popover, styles.suggest, classes?.popoverClassName),
                    modifiersCustom: [hideListener],
                    modifiers: {
                        offset: { options: { offset: [0, 2] } },
                        preventOverflow: { options: { boundary: "clippingParents", tether: true } },
                        flip: { options: { rootBoundary: "viewport" } },
                        ...popoverProps?.modifiers,
                    },
                    ...popoverProps,
                }, selectedItem: selectedItem, resetOnSelect: resetOnSelect, resetOnClose: resetOnClose, resetOnQuery: resetOnQuery, query: query }))));
};
export default Suggest;
